// 📁 src/features/backtests/runBacktest.ts
import { supabase } from '@/lib/supabaseClient';
import { normalizeTpForPersist } from '@/utils/takeProfit';

type Params = {
  userId: string; botId: string; fromDate: Date; toDate: Date; quoteCurrency: string;
  title?: string; awsBaseUrl?: string; form?: any; bot?: any; existingBacktestId?: string;
};

type FEIndicator = {
  key?: string; type?: string; name?: string; period?: number; length?: number; fast?: number; slow?: number; signal?: number;
  fastPeriod?: number; slowPeriod?: number; signalPeriod?: number; overbought?: number; oversold?: number; stdDev?: number;
  multiplier?: number; kPeriod?: number; dPeriod?: number; smooth?: number; [key: string]: any;
};

type LambdaIndicator =
  | { type: 'sma'; period: number }
  | { type: 'ema'; period: number }
  | { type: 'rsi'; period: number; overbought?: number; oversold?: number }
  | { type: 'adx'; period: number }
  | { type: 'momentum'; period: number }
  | { type: 'macd'; fastPeriod: number; slowPeriod: number; signalPeriod: number }
  | { type: 'bollinger_bands'; period: number; stdDev: number }
  | { type: 'stochastic'; kPeriod: number; dPeriod: number; smooth: number }
  | { type: 'volume'; period: number; multiplier: number }
  | { type: 'trend_lines' }
  | { type: 'price_action' }
  | { type: 'level2_data' }
  | { type: string; [key: string]: any };

type LongShortConfig = {
  enabled: boolean; strategy: string; orderType: string; dcaEnabled: boolean; dcaStages: Array<any>;
  trailingEntryEnabled: boolean; trailingEntryStages: Array<any>; profitManagementEnabled: boolean; profitManagementStages: Array<any>;
};

function normalizeIndicatorForLambda(ind: FEIndicator): LambdaIndicator | null {
  const k = (ind.key || ind.type || ind.name || '').toLowerCase().trim();
  switch (k) {
    case 'sma': return { type: 'sma', period: Number(ind.period || ind.length) };
    case 'ema': return { type: 'ema', period: Number(ind.period || ind.length) };
    case 'rsi': return { type: 'rsi', period: Number(ind.period), overbought: ind.overbought ? Number(ind.overbought) : undefined, oversold: ind.oversold ? Number(ind.oversold) : undefined };
    case 'adx': return { type: 'adx', period: Number(ind.period) };
    case 'momentum': return { type: 'momentum', period: Number(ind.period) };
    case 'macd': return { type: 'macd', fastPeriod: Number(ind.fast || ind.fastPeriod), slowPeriod: Number(ind.slow || ind.slowPeriod), signalPeriod: Number(ind.signal || ind.signalPeriod) };
    case 'bollinger bands': case 'bollinger_bands': case 'bb': return { type: 'bollinger_bands', period: Number(ind.period), stdDev: Number(ind.stdDev) };
    case 'stochastic': case 'stoch': return { type: 'stochastic', kPeriod: Number(ind.kPeriod), dPeriod: Number(ind.dPeriod), smooth: Number(ind.smooth) };
    case 'volume': return { type: 'volume', period: Number(ind.period), multiplier: Number(ind.multiplier) };
    case 'trend_lines': case 'trend lines': return { type: 'trend_lines' };
    case 'price_action': case 'price action': return { type: 'price_action' };
    case 'level2_data': case 'level2 data': return { type: 'level2_data' };
    default:
      const dynamicIndicator: any = { type: k };
      Object.keys(ind).forEach(key => { if (key !== 'key' && key !== 'type' && key !== 'name') dynamicIndicator[key] = ind[key]; });
      return dynamicIndicator;
  }
}

function normalizeStrategyKey(s?: string): string {
  const m = (s || '').toLowerCase().trim();
  if (!m) return 'indicators_only';
  if (['trend', 'trend following', 'trend_following'].includes(m)) return 'trend_following';
  if (m === 'scalping') return 'scalping';
  if (['support_resistance', 'support & resistance'].includes(m)) return 'support_resistance';
  if (['candle_patterns', 'candle patterns'].includes(m)) return 'candle_patterns';
  if (['swing', 'swing trading'].includes(m)) return 'swing';
  if (['chart_patterns', 'chart patterns'].includes(m)) return 'chart_patterns';
  if (['fvg', 'fair value gap (fvg)'].includes(m)) return 'fvg';
  return m;
}

function getLongShortConfig(form: any, bot: any, type: 'long' | 'short'): LongShortConfig {
  const prefix = type === 'long' ? 'long' : 'short';
  return {
    enabled: (form?.[`${prefix}EntryEnabled`] ?? bot?.[`${prefix}_entry_enabled`]) === true,
    strategy: (form?.[`${prefix}EntryStrategy`] ?? bot?.[`${prefix}_entry_strategy`]) || '',
    orderType: (form?.[`${prefix}OrderType`] ?? bot?.[`${prefix}_order_type`]) || '',
    dcaEnabled: (form?.[`${prefix}DcaEnabled`] ?? bot?.[`${prefix}_dca_enabled`]) === true,
    dcaStages: Array.isArray(form?.[`${prefix}DcaStages`] ?? bot?.[`${prefix}_dca_stages`]) ? (form?.[`${prefix}DcaStages`] ?? bot?.[`${prefix}_dca_stages`]) : [],
    trailingEntryEnabled: (form?.[`${prefix}TrailingEntryEnabled`] ?? bot?.[`${prefix}_trailing_entry_enabled`]) === true,
    trailingEntryStages: Array.isArray(form?.[`${prefix}TrailingEntryStages`] ?? bot?.[`${prefix}_trailing_entry_stages`]) ? (form?.[`${prefix}TrailingEntryStages`] ?? bot?.[`${prefix}_trailing_entry_stages`]) : [],
    profitManagementEnabled: (form?.[`${prefix}ProfitManagementEnabled`] ?? bot?.[`${prefix}_profit_management_enabled`]) === true,
    profitManagementStages: Array.isArray(form?.[`${prefix}ProfitManagementStages`] ?? bot?.[`${prefix}_profit_management_stages`]) ? (form?.[`${prefix}ProfitManagementStages`] ?? bot?.[`${prefix}_profit_management_stages`]) : []
  };
}

async function prepareLambdaPayloadWithPagination(form: any, bot: any, selectedCoins: string[], quoteCurrency: string, fromDate: Date, toDate: Date): Promise<any> {
  const result: any = {
    selectedCoins, quoteCurrency, fromDate: fromDate.toISOString(), toDate: toDate.toISOString(),
    primaryInterval: form?.primaryTimeInterval || bot?.primary_time_interval,
    secondaryEnabled: form?.secondaryTimeEnabled ?? bot?.secondary_time_enabled
  };

  if (result.secondaryEnabled) result.secondaryInterval = form?.secondaryTimeInterval || bot?.secondary_time_interval;

  const segmentMonths = form?.segmentMonths || bot?.segment_months;
  if (segmentMonths !== undefined && segmentMonths !== null) result.segmentMonths = Number(segmentMonths);
  else {
    const monthsDiff = Math.ceil((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24 * 30));
    result.segmentMonths = Math.max(1, Math.min(monthsDiff, 3)); 
  }
  result.segmentLimit = Number(form?.segmentLimit || bot?.segment_limit || 100);
  result.maxSegmentsParallel = Number(form?.maxSegmentsParallel || bot?.max_segments_parallel || 5);
  return result;
}

// 🔥 KLAIDA IŠTAISYTA: Pašalinti laužtiniai ir apvalūs skliaustai iš URL
const API_ENDPOINTS = ['https://wjdkhvqa24.execute-api.eu-north-1.amazonaws.com/prod/bot-backtest'];

async function callLambdaWithRetry(apiUrl: string, payload: any, maxRetries = 3): Promise<Response> {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'Origin': window.location.origin },
        body: JSON.stringify(payload), mode: 'cors', credentials: 'omit', signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      return response;
    } catch (error) {
      if (attempt === maxRetries) throw error;
      await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
    }
  }
  throw new Error('Visi API endpoint\'ai nepavyko');
}

async function tryMultipleEndpoints(payload: any): Promise<Response> {
  for (const endpoint of API_ENDPOINTS) {
    try { return await callLambdaWithRetry(endpoint, payload, 2); } catch (error) { continue; }
  }
  throw new Error('Visi API endpoint\'ai nepavyko');
}

export async function runBacktest({ userId, botId, fromDate, toDate, quoteCurrency, title, form, bot, existingBacktestId }: Params): Promise<any> {
  const quote = (quoteCurrency || '').trim().toUpperCase();
  if (!quote) throw new Error('Kotavimo valiuta yra privaloma');
  if (!form && !bot) throw new Error('Form data arba bot data yra privaloma');

  let backtestId: string;
  let clonedBotId: string | undefined;
  let action: 'created' | 'updated';
  
  // 🔥 KLAIDA IŠTAISYTA: Vieningas kintamasis `backtestTitle`
  const baseName = title || form?.botName || form?.name || bot?.name || 'Neįvardytas Bot';
  const backtestTitle = baseName;
  
  const periodObj = { from: fromDate.toISOString().slice(0, 10), to: toDate.toISOString().slice(0, 10), title: backtestTitle };

  if (existingBacktestId) {
    const { data: existingBacktest } = await supabase.from('backtest_results').select('id, deleted_at, user_id, status').eq('id', existingBacktestId).maybeSingle();
    if (existingBacktest && !existingBacktest.deleted_at && existingBacktest.user_id === userId) {
      backtestId = existingBacktestId; clonedBotId = botId; action = 'updated';
    } else {
      const { data: rpcRes, error: rpcError } = await supabase.rpc('f_run_backtest_with_quote_currency', { p_bot_id: botId, p_period: periodObj, p_quote_currency: quote, p_target: 'backtest', p_user_id: userId });
      if (rpcError) throw rpcError;
      const rpcResult = Array.isArray(rpcRes) ? rpcRes[0] : rpcRes;
      backtestId = rpcResult?.backtest_id || rpcResult?.id; clonedBotId = rpcResult?.bot_id; action = 'created';
    }
  } else {
    const { data: rpcRes, error: rpcError } = await supabase.rpc('f_run_backtest_with_quote_currency', { p_bot_id: botId, p_period: periodObj, p_quote_currency: quote, p_target: 'backtest', p_user_id: userId });
    if (rpcError) throw rpcError;
    const rpcResult = Array.isArray(rpcRes) ? rpcRes[0] : rpcRes;
    backtestId = rpcResult?.backtest_id || rpcResult?.id; clonedBotId = rpcResult?.bot_id; action = 'created';
  }

  const periodDaysVal = Math.round((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24));
  const periodLabelVal = `${periodObj.from} to ${periodObj.to}`;
  
  // Įrašome visas datas iškart, kad kortelė neturėtų tuščio brūkšniuko
  await supabase.from('backtest_results').update({
      period: periodLabelVal, period_label: periodLabelVal, period_days: periodDaysVal,
      start_date: periodObj.from, end_date: periodObj.to,
      status: 'running', updated_at: new Date().toISOString()
    }).eq('id', backtestId);
  
  const selectedCoins: string[] = (Array.isArray(form?.selectedCoins) && form.selectedCoins.length ? form.selectedCoins : Array.isArray(bot?.selected_coins) ? bot.selected_coins : []).map((c: any) => String(c).trim().toUpperCase());
  if (selectedCoins.length === 0) throw new Error('Pasirinkite bent vieną kryptovaliutą');

  const primaryInterval = form?.primaryTimeInterval ?? bot?.primary_time_interval;
  if (!primaryInterval) throw new Error('Nustatykite pagrindinį laiko intervalą');

  const strategyKey = normalizeStrategyKey(form?.strategyKey ?? bot?.strategy_key ?? form?.strategy ?? bot?.strategy);
  const indicatorsSrc: FEIndicator[] = Array.isArray(form?.primaryTimeIndicators) && form.primaryTimeIndicators.length ? form.primaryTimeIndicators : Array.isArray(bot?.primary_time_indicators) ? bot.primary_time_indicators : [];
  const indicators: LambdaIndicator[] = indicatorsSrc.map(normalizeIndicatorForLambda).filter(Boolean) as LambdaIndicator[];
  if (indicators.length === 0) throw new Error('Nustatykite bent vieną indikatorių');

  const secondaryIndicatorsSrc: FEIndicator[] = Array.isArray(form?.secondaryTimeIndicators) && form.secondaryTimeIndicators.length ? form.secondaryTimeIndicators : Array.isArray(bot?.secondary_time_indicators) ? bot.secondary_time_indicators : [];
  const secondaryTimeIndicators: LambdaIndicator[] = secondaryIndicatorsSrc.map(normalizeIndicatorForLambda).filter(Boolean) as LambdaIndicator[];

  const initialCapital = Number(form?.accountBalance ?? bot?.account_balance ?? form?.initialCapital);
  if (!initialCapital || initialCapital <= 0) throw new Error('Nustatykite teisingą pradinį kapitalą (didesnis už 0)');

  const longConfig = getLongShortConfig(form, bot, 'long');
  const shortConfig = getLongShortConfig(form, bot, 'short');
  const paginationParams = await prepareLambdaPayloadWithPagination(form, bot, selectedCoins, quote, fromDate, toDate);

  const botConfig: any = {
    strategy: strategyKey,
    primary_time_interval: primaryInterval,
    indicators: indicators,
    long_entry_enabled: form?.longEnabled ?? form?.longEntryEnabled ?? bot?.long_entry_enabled ?? bot?.long_enabled ?? true,
    short_entry_enabled: form?.shortEnabled ?? form?.shortEntryEnabled ?? bot?.short_entry_enabled ?? bot?.short_enabled ?? true,
    account_balance: initialCapital,
    quote_currency: quote,
    name: baseName,
    title: backtestTitle, 
    exchange: form?.exchange ?? bot?.exchange,
    exchange_fee_percentage: Number(form?.exchangeFeePercentage ?? bot?.exchange_fee_percentage ?? 0.055),
    
    percentage_amount: Number(form?.percentageAmount ?? bot?.percentage_amount ?? 10),
    use_fixed_amount: form?.useFixedAmount ?? bot?.use_fixed_amount ?? false,
    use_percentage: form?.usePercentage ?? bot?.use_percentage ?? true,
    amount_per_trade: Number(form?.amountPerTrade ?? bot?.amount_per_trade ?? 10),
    max_allocation: Number(form?.maxAllocation ?? bot?.max_allocation ?? 3000),
    max_daily_trades: Number(form?.maxDailyTrades ?? bot?.max_daily_trades ?? 0),
    daily_trade_limit: Number(form?.dailyTradeLimit ?? bot?.daily_trade_limit ?? 0),
    use_precision_filter: form?.usePrecisionFilter ?? bot?.use_precision_filter ?? false,
    precision_percentage: Number(form?.precisionPercentage ?? bot?.precision_percentage ?? 0),

    buy_take_profit_stages_enabled: form?.buyTakeProfitStagesEnabled ?? bot?.buy_take_profit_stages_enabled ?? false,
    buy_take_profit_stages_data: form?.buyTakeProfitStagesData ?? bot?.buy_take_profit_stages_data ?? [],
    buy_take_profit_stages_initial_trailing_stop: form?.buyTakeProfitStagesInitialTrailingStop ?? bot?.buy_take_profit_stages_initial_trailing_stop,
    
    sell_take_profit_stages_enabled: form?.sellTakeProfitStagesEnabled ?? bot?.sell_take_profit_stages_enabled ?? false,
    sell_take_profit_stages_data: form?.sellTakeProfitStagesData ?? bot?.sell_take_profit_stages_data ?? [],
    sell_take_profit_stages_initial_trailing_stop: form?.sellTakeProfitStagesInitialTrailingStop ?? bot?.sell_take_profit_stages_initial_trailing_stop
  };

  botConfig.tradingParameters = {
    longEnabled: botConfig.long_entry_enabled,
    shortEnabled: botConfig.short_entry_enabled,
    max_daily_trades: botConfig.max_daily_trades,
    percentage_amount: botConfig.percentage_amount
  };

  if (longConfig.enabled) {
    if (longConfig.strategy) botConfig.long_entry_strategy = longConfig.strategy;
    if (longConfig.orderType) botConfig.long_order_type = longConfig.orderType;
    if (longConfig.dcaEnabled !== undefined) botConfig.long_dca_enabled = longConfig.dcaEnabled;
    if (longConfig.dcaStages.length > 0) botConfig.long_dca_stages = longConfig.dcaStages;
    if (longConfig.trailingEntryEnabled !== undefined) botConfig.long_trailing_entry_enabled = longConfig.trailingEntryEnabled;
    if (longConfig.trailingEntryStages.length > 0) botConfig.long_trailing_entry_stages = longConfig.trailingEntryStages;
    if (longConfig.profitManagementEnabled !== undefined) botConfig.long_profit_management_enabled = longConfig.profitManagementEnabled;
    if (longConfig.profitManagementStages.length > 0) botConfig.long_profit_management_stages = longConfig.profitManagementStages;
  }

  if (shortConfig.enabled) {
    if (shortConfig.strategy) botConfig.short_entry_strategy = shortConfig.strategy;
    if (shortConfig.orderType) botConfig.short_order_type = shortConfig.orderType;
    if (shortConfig.dcaEnabled !== undefined) botConfig.short_dca_enabled = shortConfig.dcaEnabled;
    if (shortConfig.dcaStages.length > 0) botConfig.short_dca_stages = shortConfig.dcaStages;
    if (shortConfig.trailingEntryEnabled !== undefined) botConfig.short_trailing_entry_enabled = shortConfig.trailingEntryEnabled;
    if (shortConfig.trailingEntryStages.length > 0) botConfig.short_trailing_entry_stages = shortConfig.trailingEntryStages;
    if (shortConfig.profitManagementEnabled !== undefined) botConfig.short_profit_management_enabled = shortConfig.profitManagementEnabled;
    if (shortConfig.profitManagementStages.length > 0) botConfig.short_profit_management_stages = shortConfig.profitManagementStages;
  }

  const lambdaPayload: any = {
    botConfig: botConfig,
    pagination: paginationParams,
    userId: userId,
    backtestId: backtestId,
    botId: clonedBotId || botId,
    initialCapital: initialCapital,
    quoteCurrency: quote,
    secondaryEnabled: paginationParams.secondaryEnabled,
    interval: primaryInterval,
    startDate: fromDate.toISOString(),
    endDate: toDate.toISOString(),
    isBacktest: true,
    _payloadVersion: '10.9',
    _generatedAt: new Date().toISOString()
  };

  if (paginationParams.secondaryEnabled && paginationParams.secondaryInterval) {
    lambdaPayload.secondaryInterval = paginationParams.secondaryInterval;
  }
  if (secondaryTimeIndicators && secondaryTimeIndicators.length > 0) {
    lambdaPayload.secondaryIndicators = secondaryTimeIndicators;
  }

  const tpUi = form?.sellTakeProfit ?? { enabled: false, useFixedAmount: false, stages: [] };
  if (tpUi.enabled) {
    lambdaPayload.takeProfit = {
      enabled: true,
      useFixedAmount: tpUi.useFixedAmount,
      stages: tpUi.stages.map((s: any) => ({ stage: s.stage, sellPct: s.sell_pct || (s.sellPercentage ? s.sellPercentage/100 : 0.2), targetPct: s.target_pct || (s.profitTargetPct ? s.profitTargetPct/100 : 0.05), targetUsd: s.target_usd || s.profitAmountUsd }))
    };
  }

  if (longConfig.profitManagementStages && longConfig.profitManagementStages.length > 0) lambdaPayload.longTpConfig = longConfig.profitManagementStages;
  if (shortConfig.profitManagementStages && shortConfig.profitManagementStages.length > 0) lambdaPayload.shortTpConfig = shortConfig.profitManagementStages;

  try {
    const lambdaResponse = await tryMultipleEndpoints(lambdaPayload);
    if (!lambdaResponse.ok) throw new Error(`Lambda klaida: ${lambdaResponse.status} ${lambdaResponse.statusText}`);
  } catch (error) {
    let errorMessage = 'Backtest\'o serveris laikinai neprieinamas. Bandykite vėliau.';
    let detailedError = error instanceof Error ? error.message : 'Nežinoma klaida';
    if (detailedError.includes('500') || detailedError.includes('HTTP 500')) errorMessage = 'AWS Lambda serverio konfigūracijos klaida.';
    
    if (typeof window !== 'undefined') {
      localStorage.setItem('has_recent_backtest_errors', 'true');
      localStorage.setItem('last_backtest_error', JSON.stringify({ error: detailedError, userMessage: errorMessage, backtestId }));
    }
    
    await supabase.from('backtest_results').update({ status: 'failed', error_message: errorMessage, updated_at: new Date().toISOString() }).eq('id', backtestId);
    throw new Error(errorMessage);
  }

  return { backtestId, title: backtestTitle, botId: clonedBotId || botId, action };
}

export const pollBacktestCompletion = async (backtestId: string, options?: { timeoutMs?: number; intervalMs?: number }): Promise<void> => {
  const timeoutMs = options?.timeoutMs || 300000;
  const intervalMs = options?.intervalMs || 2000;
  const startTime = Date.now();
  
  return new Promise((resolve, reject) => {
    const checkInterval = setInterval(async () => {
      try {
        if (Date.now() - startTime > timeoutMs) {
          clearInterval(checkInterval);
          await supabase.from('backtest_results').update({ status: 'failed', error_message: `Timeout (${timeoutMs}ms)` }).eq('id', backtestId);
          reject(new Error(`Backtest stebėjimo timeout`));
          return;
        }
        const { data, error } = await supabase.from('backtest_results').select('status, error_message').eq('id', backtestId).single();
        if (error) { clearInterval(checkInterval); reject(error); return; }
        
        if (data.status === 'completed') {
          if (typeof window !== 'undefined') localStorage.removeItem('has_recent_backtest_errors');
          clearInterval(checkInterval); resolve(); return;
        }
        if (data.status === 'failed') {
          clearInterval(checkInterval); reject(new Error(`Backtest nepavyko: ${data.error_message}`)); return;
        }
      } catch (error) {
        clearInterval(checkInterval); reject(error);
      }
    }, intervalMs);
  });
};

export async function checkAndFixBacktestStatus(backtestId: string): Promise<void> {
  try {
    if (typeof window !== 'undefined') {
      const lastErrorRaw = localStorage.getItem('last_backtest_error');
      if (lastErrorRaw) {
        const lastError = JSON.parse(lastErrorRaw);
        if (lastError.backtestId === backtestId) {
          const { data } = await supabase.from('backtest_results').select('status').eq('id', backtestId).single();
          if (data && data.status === 'pending') {
            await supabase.from('backtest_results').update({ status: 'failed', error_message: lastError.userMessage || 'Klaida' }).eq('id', backtestId);
          }
        }
      }
    }
  } catch (e) {}
}